<script>
/* global axios */
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import LowBackupCodesBanner from './components/app/LowBackupCodesBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import CallDock from './components/calls/CallDock.vue';
import { setColorTheme } from './helper/themeHelper';

// atributos que ligam a inversão da paleta (ver _next-colors.scss)
const ATRIBUTOS_DE_TEMA = [
  'data-tema-claro-invertido',
  'data-tema-escuro-invertido',
];
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWhatsappCallAccess } from 'dashboard/composables/useWhatsappCallAccess';
import { useBcvozWebphone } from 'dashboard/composables/useBcvozWebphone';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useUnreadDocumentTitle } from 'dashboard/composables/useUnreadDocumentTitle';
import { useInternalChatNotifier } from 'dashboard/composables/useInternalChatNotifications';
import { aplicarIdioma } from 'dashboard/i18n/lazy';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    CallDock,
    LowBackupCodesBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();
    const { canReceiveAstracallCalls } = useWhatsappCallAccess();
    // Webphone BCVOZ global (carrega do CDN, visível em tudo). No-op se o módulo
    // bcvoz não está ligado ou não há sessão.
    const { loadBcvozWebphone } = useBcvozWebphone();
    // #26 — nº de não-lidas no título da aba
    useUnreadDocumentTitle();
    // #7 — som + notificação do navegador do Chat Interno; fica aqui (raiz do
    // dashboard) pra avisar mesmo com o usuário em outra tela
    useInternalChatNotifier();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
      canReceiveAstracallCalls,
      loadBcvozWebphone,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
      // null = ainda não lido do shell (ver applyAccountTheme)
      atributosGlobaisDoTema: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
          this.applyAccountTheme();
          this.maybeLoadBcvoz();
        }
      },
    },
    // o accountId pode resolver antes do usuário carregar; o webphone BCVOZ só
    // deve carregar com usuário logado, então reagimos também ao currentUser.
    'currentUser.id': {
      handler() {
        this.maybeLoadBcvoz();
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    // Webphone BCVOZ global: carrega o script do CDN SÓ com usuário logado e
    // conta ativa. Idempotente (o composable injeta uma vez só).
    maybeLoadBcvoz() {
      if (this.currentAccountId && this.currentUser?.id) {
        this.loadBcvozWebphone(this.currentAccountId);
      }
    },
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        // carrega o idioma antes de aplicar (ver dashboard/i18n/lazy.js); trocar
        // sem as mensagens deixaria a tela mostrando as chaves cruas
        aplicarIdioma(locale);
      }
    },
    // Tema de aparência POR CONTA: injeta o CSS do tema da conta por cima do
    // tema global (que já veio no <head> do shell). Some ao trocar p/ conta sem tema.
    async applyAccountTheme() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/appearance_theme`
        );
        // Atributos de INVERSÃO da paleta (fundo escuro no modo claro e vice-versa).
        // O shell já escreveu os do tema GLOBAL na tag <html>; se a conta tem tema
        // próprio, os dela mandam — se não tem, os globais precisam voltar, senão
        // fica a paleta invertida sem o CSS que a acompanha.
        const html = document.documentElement;
        if (this.atributosGlobaisDoTema === null) {
          this.atributosGlobaisDoTema = ATRIBUTOS_DE_TEMA.filter(a =>
            html.hasAttribute(a)
          );
        }
        const aplicar =
          data && data.css
            ? (data.html_attrs || '').split(' ').filter(Boolean)
            : this.atributosGlobaisDoTema;
        ATRIBUTOS_DE_TEMA.forEach(a => {
          if (aplicar.includes(a)) html.setAttribute(a, '');
          else html.removeAttribute(a);
        });

        let el = document.getElementById('astrachat-account-theme');
        if (data && data.css) {
          if (!el) {
            el = document.createElement('style');
            el.id = 'astrachat-account-theme';
            document.head.appendChild(el);
          }
          el.textContent = data.css;
        } else if (el) {
          el.remove();
        }
      } catch (e) {
        /* silencioso */
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <StatusBanner />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <LowBackupCodesBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
    <CallDock v-if="currentAccountId && canReceiveAstracallCalls" />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
